import { KEYBINDS, STATION_KEYS } from '../game/keyboard';
import type { StationKind } from '../net/protocol';
import type { IconName } from './icons';
import { STATIONS, type StationInfo } from './stations';

/**
 * What the top of the town screen offers, grouped by what kind of thing it is.
 *
 * ── Why this is one bar and not four ────────────────────────────────────────
 *
 * The town's controls had grown into three separate floating boxes stacked down the
 * middle of the screen: a `[ GRIND ]` sigil, a *Back to the Run* sigil under it, and a
 * workshop row under that. Three boxes, three visual languages, no statement of how they
 * relate — and the answer to *what can I do in town* was spread across all of them.
 * Asked for directly: *the top navigation bar should be one bar, divided by category like
 * workshop, town portal (if any) or grind, all with shortcut, like they are all actions or
 * places you can go in town.*
 *
 * That last clause is the design. A workshop, the stash and the run are the same *kind*
 * of thing from where the player sits — a place in town you go to — so they belong in one
 * bar with the categories named, rather than in separate widgets whose separateness
 * implied a difference that does not exist.
 *
 * ── Why the model is pure ───────────────────────────────────────────────────
 *
 * The part that can be *wrong* is the grouping and the keys: a shortcut printed on a
 * button that does not fire it is worse than no shortcut, and it renders perfectly. So
 * the arrangement is computed here from the same `STATION_KEYS` and `PANEL_KEYS` the
 * router reads, and the component only draws it. Every key on screen is by construction
 * the key that works.
 *
 * The handlers stay in the component. They are three genuinely different verbs — teleport
 * and open a panel, open a modal, walk to a gate — and modelling them as data would mean
 * a switch on an id somewhere anyway, plus a second place for the vocabulary to drift.
 */

/** Which drawer of the bar something sits in. */
export type TownGroupKind = 'workshops' | 'storage' | 'travel';

/** What a button in the bar does, dispatched by the component. */
export type TownActionKind =
  /** Stand at a station and open its panel. */
  | { type: 'station'; station: StationKind }
  /** Open the depth chooser. */
  | { type: 'grind' }
  /** Walk to the standing return gate and step through. */
  | { type: 'resume' };

export interface TownAction {
  /** Stable id, used for the test id and as the `<For>` key. */
  id: string;
  /** What the button says, or its aria-label when it renders as an icon. */
  label: string;
  /** The longer line, for the tooltip. */
  detail: string;
  /** The keyboard shortcut, printed on the button. Absent means there is none. */
  key?: string;
  icon: IconName;
  action: TownActionKind;
}

export interface TownGroup {
  kind: TownGroupKind;
  /** The category caption above the group. */
  label: string;
  actions: TownAction[];
}

/**
 * The stash is storage, not a workshop, and that is why it is not on an `F` key.
 *
 * It sits in `STATIONS` because the server sends it as a station — it is a fixture you
 * stand at, like the Forge — but it refines nothing, and grouping it with five crafting
 * benches was what let it end up **sixth in a list of five function keys with no key at
 * all**. Splitting the categories is what gave it one.
 */
const STORAGE_STATIONS: readonly StationKind[] = ['STASH'];

function isStorage(station: StationInfo): boolean {
  return STORAGE_STATIONS.includes(station.kind);
}

/**
 * The bar, in the order it is drawn.
 *
 * `present` is what the server actually sent in the ZONE frame — the town decides what
 * exists, not this file. A group with nothing in it is dropped rather than drawn empty,
 * so a layout with no stash shows no *Storage* caption.
 *
 * `hasReturnGate` is whether a suspended run is standing in town. *Back to the Run* only
 * appears when there is one: a travel button that can only refuse is worse than no button,
 * which is the same rule the death screen and the belt follow.
 */
export function townGroups(
  present: readonly { kind: StationKind }[],
  options: { hasReturnGate: boolean },
): TownGroup[] {
  const sent = new Set(present.map((s) => s.kind));
  // Iterating STATIONS rather than the frame keeps the order fixed. A row that reshuffled
  // between visits would undo the point of the town being learnable.
  const shown = STATIONS.filter((s) => sent.has(s.kind));

  const workshops = shown.filter((s) => !isStorage(s));
  const storage = shown.filter(isStorage);

  const groups: TownGroup[] = [];

  if (workshops.length > 0) {
    groups.push({
      kind: 'workshops',
      label: 'Workshops',
      actions: workshops.map((station, index) => ({
        id: station.kind,
        label: station.name,
        detail: station.domain,
        // Positional, matching the router: the nth workshop is on the nth function key.
        // A sixth workshop with no key prints nothing rather than a wrong one.
        key: STATION_KEYS[index],
        icon: station.icon,
        action: { type: 'station', station: station.kind },
      })),
    });
  }

  if (storage.length > 0) {
    groups.push({
      kind: 'storage',
      label: 'Storage',
      actions: storage.map((station) => ({
        id: station.kind,
        label: station.name,
        detail: station.domain,
        key: KEYBINDS.storage,
        icon: station.icon,
        action: { type: 'station', station: station.kind },
      })),
    });
  }

  const travel: TownAction[] = [
    {
      id: 'grind',
      label: 'Grind',
      detail: 'Choose a depth and dive',
      key: KEYBINDS.grind,
      icon: 'portal',
      action: { type: 'grind' },
    },
  ];
  if (options.hasReturnGate) {
    travel.push({
      id: 'resume',
      label: 'Back to the Run',
      detail: 'Walk to the standing gate and step through',
      key: KEYBINDS.town,
      icon: 'gate',
      action: { type: 'resume' },
    });
  }
  groups.push({ kind: 'travel', label: 'Travel', actions: travel });

  return groups;
}

/** The tooltip a button carries: what it is, and the key that does it. */
export function actionTitle(action: TownAction): string {
  const key = action.key ? ` (${action.key.toUpperCase()})` : '';
  return `${action.label}${key} — ${action.detail}`;
}
